import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { trpc } from "@/lib/trpc";
import { TaskEditSheet, type TaskEditSheetTask } from "./TaskEditSheet";
import {
  closeTaskEditSheet,
  EMPTY_TASK_EDIT_SHEET_STATE,
  openTaskEditSheet,
  type TaskEditSheetState,
} from "./taskEditSheetState";

type TaskEditSheetContextValue = {
  openTask: (task: TaskEditSheetTask) => void;
  closeTask: () => void;
};

const TaskEditSheetContext = createContext<TaskEditSheetContextValue | null>(null);

const NOOP_TASK_EDIT_SHEET_CONTEXT: TaskEditSheetContextValue = {
  openTask: () => undefined,
  closeTask: () => undefined,
};

export function useTaskEditSheet() {
  return useContext(TaskEditSheetContext) ?? NOOP_TASK_EDIT_SHEET_CONTEXT;
}

export function TaskEditSheetProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TaskEditSheetState>(EMPTY_TASK_EDIT_SHEET_STATE);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const utils = trpc.useUtils();

  const openTask = useCallback((nextTask: TaskEditSheetTask) => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    setOpen(true);
    setState(previous => openTaskEditSheet(previous, nextTask));
  }, []);

  const closeTask = useCallback(() => {
    setOpen(false);
    setState(closeTaskEditSheet());
  }, []);

  const task = state.task;
  const areaPickerOpen = state.areaPickerOpen;

  return (
    <TaskEditSheetContext.Provider value={{ openTask, closeTask }}>
      {children}
      {task && (
        <TaskEditSheet
          key={task.id}
          task={task}
          open={open}
          onOpenChange={open => {
            if (!open) {
              setOpen(false);
              if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
              closeTimer.current = window.setTimeout(() => {
                closeTimer.current = null;
                setState(closeTaskEditSheet());
              }, 350);
            }
          }}
          onAreaClick={() => setState(previous => ({ ...previous, areaPickerOpen: true }))}
          areaPickerOpen={areaPickerOpen}
          onAreaPickerOpenChange={open =>
            setState(previous => ({ ...previous, areaPickerOpen: open }))
          }
          onDuplicated={async id => {
            const duplicated = await utils.tasks.get.fetch({ id });
            setState(previous => ({ ...previous, task: duplicated as TaskEditSheetTask }));
          }}
        />
      )}
    </TaskEditSheetContext.Provider>
  );
}
